import { ExternalLink } from "@/components/ExternalLink";
import { Tables } from "@/gen/tableNames/tables";
import i18n from "@/i18n";
import { Clients } from "@/lib/clients";
import { formatNumber } from "@/features/Table/utils";
import { parseDate } from "@/lib/utils";
import { Link } from "@heroui/link";
import { ColumnDef } from "@tanstack/react-table";
import { toWorkspacePath } from "@/features/Workspaces/workspaceRoutes";

export type C3TimeSerie = {
  id: number;
  date: string;
  c3_static_datum_name: string;
  c3_static_datum_id?: number;
  fixing: number;
  market_value: number;
  currency: string;
  aum?: number;
  aum_currency?: string;
};

const tableName = Tables.C3_TIME_SERIES;

export const getC3TimeSeriesColumns = (
  t: (key: string, options?: Record<string, unknown>) => string,
): ColumnDef<C3TimeSerie>[] => [
  {
    accessorKey: "id",
    header: t(`${tableName}.columns.id`, { ns: "tables", defaultValue: "ID" }),
  },
  {
    accessorKey: "date",
    header: t(`${tableName}.columns.date`, {
      ns: "tables",
      defaultValue: "Date",
    }),
    cell: ({ row }) => {
      return parseDate(row.getValue("date"));
    },
  },
  {
    accessorKey: "c3_static_datum_name",
    header: t(`${tableName}.columns.c3_static_datum_name`, {
      ns: "tables",
      defaultValue: "Asset",
    }),
    cell: ({ row }) => {
      return (
        <ExternalLink
          href={toWorkspacePath(
            `/admin/${Clients.C3}/${Tables.C3_STATIC_DATA}/${row.original.c3_static_datum_id}`,
          )}
          className="text-xs lg:whitespace-nowrap"
          underline="hover"
        >
          {row.getValue("c3_static_datum_name")}
        </ExternalLink>
      );
    },
  },
  {
    accessorKey: "fixing",
    header: () => (
      <div className="text-right">
        {t(`${tableName}.columns.fixing`, {
          ns: "tables",
          defaultValue: "Fixing",
        })}
      </div>
    ),
    cell: ({ row }) => {
      const value = row.getValue("fixing") as number;
      const formattedValue =
        value === null || value === undefined || Number.isNaN(value)
          ? ""
          : new Intl.NumberFormat("en-US", {
              minimumFractionDigits: 5,
              maximumFractionDigits: 5,
            }).format(value);

      return <div className="text-right">{formattedValue}</div>;
    },
  },
  {
    accessorKey: "market_value",
    header: () => (
      <div className="text-right">
        {t(`${tableName}.columns.market_value`, {
          ns: "tables",
          defaultValue: "Market value",
        })}
      </div>
    ),
    cell: ({ row }) => {
      return (
        <div className="text-right">
          {formatNumber(row.getValue("market_value"))}
        </div>
      );
    },
  },
  {
    accessorKey: "currency",
    header: t(`${tableName}.columns.currency`, {
      ns: "tables",
      defaultValue: "Currency",
    }),
  },
  {
    accessorKey: "aum",
    header: () => (
      <div className="text-right">
        {t(`${tableName}.columns.aum`, {
          ns: "tables",
          defaultValue: "AUM",
        })}
      </div>
    ),
    cell: ({ row }) => {
      return (
        <div className="text-right">
          {formatNumber(row.getValue("aum"))}
        </div>
      );
    },
  },
  {
    accessorKey: "aum_currency",
    header: t(`${tableName}.columns.aum_currency`, {
      ns: "tables",
      defaultValue: "AUM Currency",
    }),
  },
  {
    id: "actions",
    header: () => (
      <div className="text-right">
        {t(`${tableName}.columns.actions`, {
          ns: "tables",
          defaultValue: "Actions",
        })}
      </div>
    ),
    cell: ({ row }) => {
      return (
        <div className="flex justify-end">
          <Link
            href={`${window.location.pathname}/${row.getValue("id")}`}
            className="text-xs"
            underline="hover"
          >
            {i18n.t("actions.edit")}
          </Link>
        </div>
      );
    },
  },
];
